'use client'

import { useTranslations } from 'next-intl'
import { env } from '@/lib/runtime-env'
import { useCallback, useState } from 'react'

import PopoverMenu from '@/components/PopoverMenu'
import ViverseButton, { type ViverseButtonProps } from '@/components/ViverseButton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

const DEFAULT_CREATOR_STUDIO_URL = 'https://studio.viverse.com'

type CreateForFreeMenuButtonProps = Omit<ViverseButtonProps, 'children' | 'onClick'> & {
  label?: string
}

function CreateForFreeMenuButton({
  label,
  className,
  variant = 'default',
  ...buttonProps
}: CreateForFreeMenuButtonProps) {
  const t = useTranslations()
  const creatorStudioUrl = env('NEXT_PUBLIC_CREATOR_STUDIO_URL') || DEFAULT_CREATOR_STUDIO_URL
  const [open, setOpen] = useState(false)

  const handleSelect = useCallback(() => {
    setOpen(false)
  }, [])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ViverseButton {...buttonProps} variant={variant} className={cn('gap-2', className)}>
          {label ?? t('creator-intro.banner.cta-1')}
        </ViverseButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="min-w-(--radix-popover-trigger-width) border-none bg-transparent p-0"
      >
        <PopoverMenu className="bg-vive-primary-70/50 w-full rounded-[5px] p-2 backdrop-blur-xl">
          <PopoverMenu.Group>
            <a
              href={`${creatorStudioUrl}/content-management?create=experience`}
              onClick={handleSelect}
            >
              <PopoverMenu.Item className="active:bg-vive-primary-60 w-full">
                <span className="body-1 text-inherit">
                  {t('creator-intro.build.create-menu.new-experience')}
                </span>
              </PopoverMenu.Item>
            </a>
            <a href={`${creatorStudioUrl}/content-management?create=game`} onClick={handleSelect}>
              <PopoverMenu.Item className="active:bg-vive-primary-60 w-full">
                <span className="body-1 text-inherit">
                  {t('creator-intro.build.create-menu.new-games')}
                </span>
              </PopoverMenu.Item>
            </a>
            <a href={`${creatorStudioUrl}/content-management?create=video`} onClick={handleSelect}>
              <PopoverMenu.Item className="active:bg-vive-primary-60 w-full">
                <span className="body-1 text-inherit">
                  {t('creator-intro.build.create-menu.new-videos')}
                </span>
              </PopoverMenu.Item>
            </a>
          </PopoverMenu.Group>
        </PopoverMenu>
      </PopoverContent>
    </Popover>
  )
}

export default CreateForFreeMenuButton
